import { useState } from 'react'
import { Base } from '@itsrever/design-system'
import moment from 'moment'
import { useCountriesReturnRate } from '@/application/queries'
import { Trans, useTranslation } from 'react-i18next'
import { CountriesReturnRateTable } from './countries-return-rate-table'
import { CountryReturnRate } from '@/domain/returns'
import { ReturnsByCompensationMethodsChart } from './returns-by-compensation-methods-chart'
import { ReverCard, ReverLoading } from '@itsrever/design-system'
import { NoData } from '@/ui/shared/no-data'
import { AreaReturnRateGraph } from './area-return-rate-graph'
import { ChartMetricTabs } from './chart-metric-tabs'

type OrderValue = keyof CountryReturnRate

const { InfoTooltip, MonthPicker } = Base

export function ReturnRateByCountry() {
  const { t } = useTranslation()
  const [chartMetric, setChartMetric] = useState<
    | 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    | 'RETURN_RATE_CHART_METRIC_QUANTITY'
    | 'RETURN_RATE_CHART_METRIC_AMOUNT'
  >('RETURN_RATE_CHART_METRIC_QUANTITY')

  const [from, setFrom] = useState<moment.Moment | null>(
    moment().subtract(1, 'M').startOf('month')
  )
  const [to, setTo] = useState<moment.Moment | null>(
    moment().subtract(1, 'M').endOf('month')
  )

  const { countriesReturnRate, shopCurrency, loading } = useCountriesReturnRate(
    from?.format('YYYY-MM-DD'),
    to?.format('YYYY-MM-DD'),
    chartMetric
  )

  const perPage = 10
  const [pageNumber, setPageNumber] = useState(1)
  const [orderValue, setOrderValue] = useState<OrderValue>('itemsPurchased')
  const [orderDirection, setOrderDirection] = useState(false)

  // Sort the whole list first: a column click must reorder every page, not just this one.
  const valuesToDisplay = [...countriesReturnRate]
    .sort((a: CountryReturnRate, b: CountryReturnRate) => {
      const valueA = (a[orderValue] as number | undefined) ?? 0
      const valueB = (b[orderValue] as number | undefined) ?? 0
      return orderDirection ? valueA - valueB : valueB - valueA
    })
    .slice(perPage * (pageNumber - 1), perPage * pageNumber)

  const changeChartMetric = (metric: typeof chartMetric) => {
    setChartMetric(metric)
    setPageNumber(1)
  }

  const noData = !valuesToDisplay.length

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full justify-end">
        <div className="w-60">
          <MonthPicker
            mode="range"
            selected={{
              from: from?.toDate(),
              to: to?.toDate()
            }}
            onSelect={(range) => {
              setFrom(range?.from ? moment(range.from) : null)
              setTo(range?.to ? moment(range.to) : null)
              setPageNumber(1)
            }}
            disabled={(month) => moment(month).isAfter(moment(), 'month')}
            placeholder={t('date_range.select_month_range')}
          />
        </div>
      </div>
      <div className="grid w-full grid-cols-1 gap-4 xl:grid-cols-2">
        <ReverCard className="h-[425px] p-3" fullWidth>
          <div className="h-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <p className="ml-2 mr-1">
                  {t('returns_analytics.return_rate_country')}
                </p>
                <InfoTooltip
                  label={t('returns_analytics.about_return_rate_country')}
                  content={
                    <Trans
                      i18nKey="returns_analytics.chart_title_tooltip_rate"
                      components={{
                        b: <b />,
                        br: <br />
                      }}
                    />
                  }
                />
              </div>
            </div>
            {loading ? (
              <div className="flex h-full flex-col justify-center">
                <ReverLoading />
              </div>
            ) : noData ? (
              <NoData />
            ) : (
              <AreaReturnRateGraph
                returnRates={valuesToDisplay.map(
                  (entry) => entry.returnRate ?? 0
                )}
                refundRates={valuesToDisplay.map(
                  (entry) => entry.refundRate ?? 0
                )}
                names={valuesToDisplay.map((entry) => entry.country)}
                labelRotation={-45}
              />
            )}
          </div>
        </ReverCard>
        <ReverCard className="h-[425px] p-3" fullWidth>
          <div className="h-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <p className="ml-2 mr-1">
                  {t('returns_analytics.return_rate_country_split')}
                </p>
                <InfoTooltip
                  label={t('returns_analytics.about_return_rate_country_split')}
                  content={
                    <Trans
                      i18nKey="returns_analytics.chart_title_tooltip_split"
                      components={{
                        b: <b />,
                        br: <br />
                      }}
                    />
                  }
                />
              </div>
              <ChartMetricTabs
                selected={chartMetric}
                setSelected={changeChartMetric}
              />
            </div>
            {loading ? (
              <div className="flex h-full flex-col justify-center">
                <ReverLoading />
              </div>
            ) : noData ? (
              <NoData />
            ) : (
              <ReturnsByCompensationMethodsChart
                itemsPurchased={valuesToDisplay.map(
                  (entry) => entry.itemsPurchased
                )}
                itemsReturned={valuesToDisplay.map(
                  (entry) =>
                    entry.itemsReturnedSplit ?? {
                      returned_refunds: 0,
                      returned_gift_cards: 0,
                      returned_exchanges: 0
                    }
                )}
                names={valuesToDisplay.map((entry) => entry.country)}
                chartMetric={chartMetric}
                currency={shopCurrency}
              />
            )}
          </div>
        </ReverCard>
      </div>
      {loading ? null : (
        <CountriesReturnRateTable
          entries={valuesToDisplay}
          totalEntries={countriesReturnRate.length}
          perPage={perPage}
          pageNumber={pageNumber}
          orderValue={orderValue}
          orderDirection={orderDirection}
          setOrderDirection={setOrderDirection}
          setOrderValue={setOrderValue}
          setPageNumber={setPageNumber}
          chartMetric={chartMetric}
          shopCurrency={shopCurrency}
        />
      )}
    </div>
  )
}
